/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components/slots';
import type { ProviderProps } from '@/lib/types';
import type { SearchFieldProps } from './types';

/**
 * Context for SearchField component
 *
 * Provides context for SearchField component to share props
 */
export const SearchFieldContext =
  createContext<ContextValue<SearchFieldProps, HTMLDivElement>>(null);

/**
 * Provider for SearchField component
 *
 * Allows setting default props for all SearchField components within
 *
 * @param props - Provider props including children and default SearchFieldProps.
 * @returns The provider component wrapping children with context.
 */
export function SearchFieldProvider({
  children,
  ...props
}: ProviderProps<SearchFieldProps>) {
  return (
    <SearchFieldContext.Provider value={props}>
      {children}
    </SearchFieldContext.Provider>
  );
}
